import { useEffect, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { runAssetsApi } from '../../lib/runAssets';

interface Props {
  runId: string;
  path: string | null;
  onClose: () => void;
}

const EXT_TO_LANG: Record<string, string> = {
  '.ts': 'typescript',
  '.tsx': 'tsx',
  '.js': 'javascript',
  '.jsx': 'jsx',
  '.json': 'json',
  '.py': 'python',
  '.lua': 'lua',
  '.cs': 'csharp',
  '.java': 'java',
  '.c': 'c',
  '.cc': 'cpp',
  '.cpp': 'cpp',
  '.h': 'c',
  '.hpp': 'cpp',
  '.go': 'go',
  '.rs': 'rust',
  '.rb': 'ruby',
  '.kt': 'kotlin',
  '.kts': 'kotlin',
  '.swift': 'swift',
  '.sh': 'bash',
  '.ps1': 'powershell',
  '.bat': 'batch',
  '.html': 'markup',
  '.htm': 'markup',
  '.xml': 'markup',
  '.svg': 'markup',
  '.css': 'css',
  '.scss': 'scss',
  '.less': 'less',
  '.md': 'markdown',
  '.yml': 'yaml',
  '.yaml': 'yaml',
  '.toml': 'toml',
  '.sql': 'sql',
};

function guessLanguage(path: string | null): string {
  if (!path) return 'text';
  const lower = path.toLowerCase();
  for (const ext in EXT_TO_LANG) {
    if (lower.endsWith(ext)) return EXT_TO_LANG[ext];
  }
  return 'text';
}

/**
 * Modal source viewer with Prism syntax highlighting. Cloned from
 * `source-code-helper-main/frontend/src/components/CriticalFilesViewer.tsx`'s
 * inline modal but driven off our `/api/runs/:id/files/read` endpoint.
 */
export function SourceCodeViewer({ runId, path, onClose }: Props) {
  const [content, setContent] = useState<string | null>(null);
  const [size, setSize] = useState<number | null>(null);
  const [binary, setBinary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!path) return;
    setContent(null);
    setSize(null);
    setBinary(false);
    setError(null);
    setLoading(true);
    runAssetsApi
      .readFile(runId, path)
      .then((data) => {
        setContent(data.content);
        setSize(data.size);
        setBinary(data.binary);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [runId, path]);

  if (!path) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal source-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        <div className="row" style={{ marginBottom: 12 }}>
          <code className="source-modal-path">{path}</code>
          {size !== null && (
            <span className="muted small" style={{ marginLeft: 8 }}>
              {(size / 1024).toFixed(1)} kB
            </span>
          )}
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
        {loading && <p className="muted">Loading...</p>}
        {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}
        {!loading && !error && binary && (
          <p className="muted">Binary file - preview not available.</p>
        )}
        {!loading && !error && !binary && content !== null && (
          <div className="code-viewer">
            <SyntaxHighlighter
              style={oneDark}
              language={guessLanguage(path)}
              showLineNumbers
              wrapLongLines
              customStyle={{ background: 'transparent', margin: 0, fontSize: 12 }}
            >
              {content}
            </SyntaxHighlighter>
          </div>
        )}
      </div>
    </div>
  );
}
